import { ButtonBase, Stack, Typography } from '@mui/material';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import LinkedinAccountAvatarCarousel from '../inbox/linkedin-account-avatar-carousel';
import { LinkedinAccount } from '../linkedin-accounts/linkedin-accounts.types';
import { Tabs, Tasks } from './task-manager.types';

interface ItemProps {
  text: string;
  count: number | undefined;
  active: boolean;

  activeTaskFilterChange(): void;
}

const TabItem: React.FC<ItemProps> = ({ text, count, active, activeTaskFilterChange }) => {
  const { classes, cx } = useStyles();
  return (
    <ButtonBase className={cx(classes.tabItem, active ? 'active' : undefined)} onClick={activeTaskFilterChange}>
      <Typography className={classes.tabItemText}>{text}</Typography>
      {!_.isNil(count) ? <Typography className={classes.tabItemCount}>{count}</Typography> : null}
    </ButtonBase>
  );
};

interface Props {
  linkedinAccounts: LinkedinAccount[];
  activeLinkedinAccounts: string[];
  activeTaskFilter: Tabs;
  tasksRefetching: boolean;
  taskCounts: Tasks;

  handleSelectAllLinkedinAccounts(): void;

  handleUnselectAllLinkedinAccounts(): void;

  handleSelectedLinkedinAccountChange(id: string): () => void;

  activeTaskFilterChange(newFilter: Tabs): () => void;
}

const TmHeader: React.FC<Props> = ({
  linkedinAccounts = [],
  activeLinkedinAccounts = [],
  activeTaskFilter,
  tasksRefetching,
  taskCounts,
  handleSelectAllLinkedinAccounts,
  handleUnselectAllLinkedinAccounts,
  handleSelectedLinkedinAccountChange,
  activeTaskFilterChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const isLinkedinAccountActive = (account: LinkedinAccount) => {
    return _.includes(activeLinkedinAccounts, account._id);
  };

  const countForTab = (tab: Tabs) => {
    switch (tab) {
      case Tabs.ALL:
        return taskCounts.totalCount;
      case Tabs.FIX_ACCOUNTS:
        return taskCounts.fixAccountsCount;
      case Tabs.APPROVE_MESSAGES:
        return taskCounts.approveMessagesCount;
      case Tabs.REPLY_MESSAGES:
        return taskCounts.replyMessagesCount;
      default:
        return undefined;
    }
  };

  return (
    <Stack gap={2.5}>
      <Stack direction={'row'} alignItems={'flex-end'} justifyContent={'space-between'} gap={3} flexWrap={'wrap'}>
        <Stack gap={0.75}>
          <Typography className={classes.title}>{t('tasksTitle')}</Typography>
          <Typography className={classes.subtitle}>
            {t('tasksSubtitle', { openTasks: taskCounts.totalCount ?? 0, accountCount: linkedinAccounts.length })}
          </Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={2.5} sx={{ minHeight: '40px' }}>
          <PaiHelpIcon tooltipText={t('manageMultipleAccountsTooltip')} />
          {linkedinAccounts.length !== activeLinkedinAccounts.length ? (
            <PaiLink text={t('selectAllAccounts')} size={'lg'} variant={'default'} underline={'always'} action={handleSelectAllLinkedinAccounts} />
          ) : (
            <PaiLink text={t('unselectAllAccounts')} size={'lg'} variant={'default'} underline={'always'} action={handleUnselectAllLinkedinAccounts} />
          )}
          <LinkedinAccountAvatarCarousel
            linkedinAccounts={linkedinAccounts}
            isLinkedinAccountActive={isLinkedinAccountActive}
            handleSelectedLinkedinAccountChange={handleSelectedLinkedinAccountChange}
          />
        </Stack>
      </Stack>
      <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
        {_.map(Tabs, (tab, index) => (
          <TabItem
            key={index}
            text={t(tab)}
            count={countForTab(tab)}
            active={activeTaskFilter === tab}
            activeTaskFilterChange={activeTaskFilterChange(tab)}
          />
        ))}
        {tasksRefetching ? <PaiDotLoader size={'small'} inline={true} /> : null}
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  title: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  tabItem: {
    height: '36px',
    padding: '0 16px',
    borderRadius: '18px',
    background: '#FFF',
    color: theme.palette.greys.dark,
    gap: '8px',
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&.active': {
      background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
      color: '#FFF',
      '& .MuiTypography-root': {
        color: '#FFF',
      },
    },
  },
  tabItemText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    color: 'inherit',
  },
  tabItemCount: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
    opacity: 0.85,
  },
}));

export default TmHeader;
